import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  doc,
  onSnapshot as onDocSnap,
  getDoc,
} from "firebase/firestore";
import AgentOwnerBadge from "../../components/AgentOwnerBadge";
import PayoutDrawer from "../../components/PayoutDrawer";

/* ───────── helpers ───────── */

function tsMs(x) {
  return x?.toDate?.()?.getTime?.() || (x ? new Date(x).getTime() : 0);
}

/* Fancy pills for lucky numbers */
function LuckyNumberPills({ numbers }) {
  if (!Array.isArray(numbers) || !numbers.length) {
    return <span className="text-slate-400">—</span>;
  }
  const nums = [...new Set(numbers)].sort((a, b) => a - b); // uniq + sort
  const size =
    nums.length > 16
      ? "w-7 h-7 text-[11px]"
      : nums.length > 10
      ? "w-8 h-8 text-[12px]"
      : "w-9 h-9 text-[13px]";

  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {nums.map((n) => (
        <span
          key={n}
          title={`Lucky ${String(n).padStart(2, "0")}`}
          className={`inline-flex items-center justify-center ${size} rounded-full
                      bg-gradient-to-b from-lime-300 to-lime-400 text-black
                      font-extrabold ring-1 ring-lime-500/40
                      shadow-[inset_0_-1px_0_rgba(0,0,0,.15)]`}
        >
          {String(n).padStart(2, "0")}
        </span>
      ))}
    </div>
  );
}

function currencySym(code) {
  switch (code) {
    case "LKR":
      return "Rs";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "INR":
      return "₹";
    default:
      return code || "$";
  }
}
const pad2 = (n) => String(n).padStart(2, "0");

/** best available timestamp for a ticket (for sorting & “today” filter) */
function bestDate(t) {
  return (
    t.paymentApprovedAt?.toDate?.() ||
    t.allocatedAt?.toDate?.() ||
    t.requestedAt?.toDate?.() ||
    t.createdAt?.toDate?.() ||
    null
  );
}
/** compare two dates for same local day */
function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildTodayNumberFreq(tickets) {
  const today = new Date();
  const counts = new Map();

  for (const t of tickets) {
    // Only count after admin confirms (paid)
    if (t.status !== "payment_approved") continue;

    const d =
      t.paymentApprovedAt?.toDate?.() ||
      t.allocatedAt?.toDate?.() ||
      t.createdAt?.toDate?.() ||
      null;
    if (!d) continue;
    if (
      d.getFullYear() !== today.getFullYear() ||
      d.getMonth() !== today.getMonth() ||
      d.getDate() !== today.getDate()
    ) {
      continue;
    }

    const nums = Array.isArray(t.numbers) ? t.numbers : [];
    for (const n of nums) counts.set(n, (counts.get(n) || 0) + 1);
  }

  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .map(([num, cnt]) => ({ num, cnt }));
}

function useCountdown(targetTs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetTs) return { expired: false, text: "" };
  const t =
    (targetTs?.toDate?.()?.getTime?.() || new Date(targetTs).getTime() || 0) -
    now;
  if (t <= 0) return { expired: true, text: "00:00" };
  const m = Math.floor(t / 60000);
  const s = Math.floor((t % 60000) / 1000);
  return { expired: false, text: `${pad2(m)}:${pad2(s)}` };
}

function StatusPill({ kind, children }) {
  const map = {
    win: "bg-emerald-500/20 text-emerald-300",
    loss: "bg-slate-500/20 text-slate-200",
    cancelled: "bg-red-500/20 text-red-200",
    waiting: "bg-yellow-500/20 text-yellow-200",
    paid: "bg-blue-500/20 text-blue-200",
    submitted: "bg-indigo-500/20 text-indigo-200",
  };
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-semibold ${
        map[kind] || "bg-slate-600/30 text-slate-200"
      }`}
    >
      {children}
    </span>
  );
}

/* small pill for the summary strip */
function NumberStatPill({ n, c }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-800 ring-1 ring-slate-700 text-xs font-semibold">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 ring-1 ring-slate-700">
        {String(n).padStart(2, "0")}
      </span>
      ×{c}
    </span>
  );
}

/* ---------- tiny agent chip & modal ---------- */
function AgentChip({ name, onClick, align = "right" }) {
  if (!name) return null;
  return (
    <button
      onClick={onClick}
      className={`text-[12px] text-slate-300 underline underline-offset-2 hover:text-white ${
        align === "right" ? "ml-auto" : ""
      }`}
      title="View agent details"
    >
      Agent: {name}
    </button>
  );
}

/* ───────── rows ───────── */
function TicketRowActive({ t, card, onAgentOpen }) {
  const nav = useNavigate();
  const sym = currencySym(t.currency);
  const picked = Array.isArray(t.numbers) ? t.numbers : [];
  const amount = Number(t.amount || (t.unitPrice || 0) * picked.length);
  const createdAt =
    t.requestedAt?.toDate?.() || t.createdAt?.toDate?.() || null;

  const isApproved = t.status === "agent_approved";
  const isSubmitted = t.status === "payment_submitted";
  const isPaid = t.status === "payment_approved";
  const cd = useCountdown(isApproved ? t.paymentExpireAt : null);

  const agentUid =
    card?.agentUid || card?.ownerUid || t.agentUid || t.allocatedBy || null;
  const agentName =
    card?.agentName || card?.ownerName || t.agentName || "Agent";

  return (
    <div className="p-4 bg-slate-800 rounded-2xl ring-1 ring-slate-700">
      <div className="flex items-center justify-between gap-3 mt-2">
        <div className="font-semibold truncate">
          Card #{String(t.cardId || "").slice(0, 8)}…
        </div>
        {t.status === "requested" && (
          <StatusPill kind="waiting">Awaiting agent approval</StatusPill>
        )}
        {isApproved && (
          <StatusPill kind="waiting">
            Pay within {cd.text}
            {cd.expired ? " (time up)" : ""}
          </StatusPill>
        )}
        {isSubmitted && (
          <StatusPill kind="submitted">Payment submitted</StatusPill>
        )}
        {isPaid && (
          <StatusPill kind="paid">
            {t.allocatedBy
              ? "Allocated by agent — paid"
              : "Paid — awaiting draw"}
          </StatusPill>
        )}
      </div>

      <div className="mt-2 text-sm text-slate-300">
        Numbers:
        <LuckyNumberPills numbers={picked} />
      </div>

      <div className="mt-1 text-sm text-slate-300 flex flex-wrap gap-x-6 gap-y-1">
        <span>
          Amount:{" "}
          <b>
            {sym}
            {amount.toFixed(2)}
          </b>
        </span>
        {createdAt ? (
          <span>Requested: {createdAt.toLocaleString()}</span>
        ) : null}
      </div>

      {/* existing badge block */}
      {card && (
        <div className="mb-4 mt-5">
          <AgentOwnerBadge card={card} />
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        {isApproved && !cd.expired && (
          <button
            onClick={() => nav(`/lottery/pay/${t.id}`)}
            className="px-3 py-2 rounded-xl bg-lime-400 text-black font-semibold"
          >
            Pay now & upload slip
          </button>
        )}
        {isApproved && cd.expired && (
          <div className="text-sm text-red-300">
            Time up. If numbers were released, pick again.
          </div>
        )}
        {isSubmitted && (
          <div className="text-sm text-slate-300">
            We’re reviewing your proof. You’ll be notified.
          </div>
        )}
        {t.status === "requested" && (
          <div className="text-sm text-slate-300">
            Hang tight — you’ll get payment details after approval.
          </div>
        )}
        <div className="ml-auto">
          <AgentChip
            name={agentName}
            onClick={() => agentUid && onAgentOpen(agentUid)}
          />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs">
        <div className="px-3 py-2 bg-green-700 rounded-xl">
          <Link to={`/card/${t.cardId}`} className=" text-slate-300">
            View card (buyers & holds)
          </Link>
        </div>
        <div className="px-3 py-2 bg-yellow-700 rounded-xl">
          <Link to={`/my-tickets/${t.id}`} className=" text-slate-300">
            View ticket details
          </Link>
        </div>
      </div>
    </div>
  );
}

function lastChangeTs(t) {
  const pick = (x) => x?.toDate?.()?.getTime?.() || 0;
  return Math.max(
    pick(t.updatedAt),
    pick(t.paymentApprovedAt),
    pick(t.paymentSubmittedAt),
    pick(t.agentApprovedAt),
    pick(t.declinedAt),
    pick(t.expiredAt),
    pick(t.allocatedAt),
    pick(t.requestedAt),
    pick(t.createdAt)
  );
}

// function TicketRowHistory({ t, card, onAgentOpen }) {
//   const sym = currencySym(t.currency);
//   const picked = Array.isArray(t.numbers) ? t.numbers : [];
//   const amount = Number(t.amount || (t.unitPrice || 0) * picked.length);
//   const drawnAt = card?.drawnAt?.toDate?.() || null;

//   const hasDrawn =
//     card?.status === "drawn" && typeof card?.winningNumber === "number";
//   const didWin =
//     hasDrawn &&
//     picked.includes(card.winningNumber) &&
//     t.status === "payment_approved";

//   let winAmount = null;
//   if (didWin && Array.isArray(card?.winners)) {
//     const me = card.winners.find((w) => w.ticketId === t.id);
//     if (me?.amount != null) winAmount = Number(me.amount);
//   }

//   let pillKind = "cancelled";
//   let pillText = "Cancelled";
//   if (t.status === "expired") pillText = "Expired";
//   if (t.status === "payment_declined") pillText = "Declined";
//   if (t.status === "void_drawn") pillText = "Voided (draw)";
//   if (hasDrawn && t.status === "payment_approved") {
//     if (didWin) {
//       pillKind = "win";
//       pillText = "WIN";
//     } else {
//       pillKind = "loss";
//       pillText = "LOSS";
//     }
//   }

//   const agentUid =
//     card?.agentUid || card?.ownerUid || t.agentUid || t.allocatedBy || null;
//   const agentName =
//     card?.agentName || card?.ownerName || t.agentName || "Agent";

//   return (
//     <div className="p-4 bg-slate-800 rounded-2xl ring-1 ring-slate-700">
//       <div className="flex items-center justify-between gap-3">
//         <div className="font-semibold truncate">
//           Card #{String(t.cardId || "").slice(0, 8)}…
//         </div>
//         <StatusPill kind={pillKind}>{pillText}</StatusPill>
//       </div>

//       <div className="mt-2 text-sm text-slate-300">
//         Numbers:
//         <LuckyNumberPills numbers={picked} />
//       </div>

//       <div className="mt-1 text-sm text-slate-300 flex flex-wrap gap-x-6 gap-y-1">
//         <span>
//           Amount:{" "}
//           <b>
//             {sym}
//             {amount.toFixed(2)}
//           </b>
//         </span>
//         {hasDrawn ? (
//           <>
//             <span>
//               Winning number:{" "}
//               <b className="px-2 py-0.5 rounded bg-yellow-300 text-black">
//                 {String(card.winningNumber).padStart(2, "0")}
//               </b>
//             </span>
//             {drawnAt ? <span>Drawn: {drawnAt.toLocaleString()}</span> : null}
//           </>
//         ) : null}
//       </div>

//       {didWin && (
//         <div className="mt-2 text-sm">
//           <span className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 font-semibold">
//             Payout:{" "}
//             {winAmount != null
//               ? `${sym}${winAmount.toFixed(2)}`
//               : "See agent payout"}
//           </span>
//         </div>
//       )}

//       <div className="mt-3 flex items-center gap-4 text-xs">
//         <Link to={`/card/${t.cardId}`} className="underline text-slate-300">
//           View card (buyers & holds)
//         </Link>
//         <Link to={`/my-tickets/${t.id}`} className="underline text-slate-300">
//           View ticket details
//         </Link>

//         <div className="ml-auto">
//           <AgentChip
//             name={agentName}
//             onClick={() => agentUid && onAgentOpen(agentUid)}
//           />
//         </div>
//       </div>
//     </div>
//   );
// }

function TicketRowHistory({ t, card, user }) {
  const [payout, setPayout] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const sym = (code) =>
    code === "LKR"
      ? "Rs"
      : code === "USD"
      ? "$"
      : code === "EUR"
      ? "€"
      : code === "GBP"
      ? "£"
      : code === "INR"
      ? "₹"
      : code || "$";

  const picked = Array.isArray(t.numbers) ? t.numbers : [];
  const amount = Number(t.amount || (t.unitPrice || 0) * picked.length);
  const drawnAt = card?.drawnAt?.toDate?.() || null;

  const hasDrawn =
    card?.status === "drawn" && typeof card?.winningNumber === "number";
  const didWin =
    hasDrawn &&
    picked.includes(card.winningNumber) &&
    t.status === "payment_approved";

  useEffect(() => {
    if (!didWin) return;
    const payoutId = `${t.cardId}_${t.id}`;
    const un = onSnapshot(doc(db, "payouts", payoutId), (s) =>
      setPayout(s.exists() ? { id: s.id, ...s.data() } : null)
    );
    return () => un();
  }, [didWin, t.cardId, t.id]);

  let pillKind = "cancelled";
  let pillText = "Cancelled";
  if (t.status === "expired") pillText = "Expired";
  if (t.status === "payment_declined") pillText = "Declined";
  if (t.status === "void_drawn") pillText = "Voided (draw)";
  if (hasDrawn && t.status === "payment_approved") {
    if (didWin) {
      pillKind = "win";
      pillText = "WIN";
    } else {
      pillKind = "loss";
      pillText = "LOSS";
    }
  }

  // payout status label
  const payoutStatus = useMemo(() => {
    if (!didWin) return null;
    if (!payout)
      return { text: "Awaiting payout", color: "bg-slate-700 text-white" };
    if (payout.paid && !payout.received)
      return { text: "Paid — confirm", color: "bg-yellow-400 text-black" };
    if (payout.received)
      return { text: "Received", color: "bg-emerald-400 text-black" };
    return { text: "Awaiting payout", color: "bg-slate-700 text-white" };
  }, [didWin, payout]);

  return (
    <div className="p-4 bg-slate-800 rounded-2xl ring-1 ring-slate-700">
      <div className="flex items-center justify-between gap-3">
        <div className="font-semibold truncate">
          Card #{String(t.cardId || "").slice(0, 8)}…
        </div>
        <StatusPill kind={pillKind}>{pillText}</StatusPill>
      </div>

      <div className="mt-2 text-sm text-slate-300">
        Numbers:
        <LuckyNumberPills numbers={picked} />
      </div>

      <div className="mt-1 text-sm text-slate-300 flex flex-wrap gap-x-6 gap-y-1">
        <span>
          Amount:{" "}
          <b>
            {sym(t.currency)}
            {amount.toFixed(2)}
          </b>
        </span>
        {hasDrawn ? (
          <>
            <span>
              Winning number:{" "}
              <b className="px-2 py-0.5 rounded bg-yellow-300 text-black">
                {String(card.winningNumber).padStart(2, "0")}
              </b>
            </span>
            {drawnAt ? <span>Drawn: {drawnAt.toLocaleString()}</span> : null}
          </>
        ) : null}
      </div>

      {/* payout chip */}
      {didWin && (
        <div className="mt-2 flex items-center gap-3">
          <span
            className={`px-2 py-1 rounded text-xs font-bold ${payoutStatus.color}`}
          >
            {payoutStatus.text}
          </span>
          <button
            className="text-xs underline text-slate-300"
            onClick={() => setDrawerOpen(true)}
          >
            View payout
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center gap-4 text-xs">
        <Link to={`/card/${t.cardId}`} className="underline text-slate-300">
          View card (buyers & holds)
        </Link>
        <Link to={`/my-tickets/${t.id}`} className="underline text-slate-300">
          View ticket details
        </Link>
      </div>

      {/* Drawer */}
      {didWin && drawerOpen && (
        <PayoutDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          payoutId={`${t.cardId}_${t.id}`}
          user={user}
        />
      )}
    </div>
  );
}

function PayBundleRow({ bundle, currencyCode = "LKR", onWhatsApp }) {
  const sym = currencySym(currencyCode);
  const now = Date.now();
  const msLeft = Math.max(0, (bundle.earliestExpireMs || 0) - now);
  const m = Math.floor(msLeft / 60000);
  const s = Math.floor((msLeft % 60000) / 1000);
  const badge =
    msLeft > 0
      ? `Pay within ${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : "Time up";

  return (
    <div className="p-4 bg-slate-800 rounded-2xl ring-1 ring-slate-700">
      <div className="flex items-center justify-between mb-1">
        <div className="font-semibold truncate">
          {bundle.agentName || "Agent"} (x{bundle.tickets.length} tickets)
        </div>
        <span className="px-2 py-1 rounded bg-slate-700 text-xs">{badge}</span>
      </div>

      <div className="text-sm text-slate-300">
        Numbers total: <b>{bundle.totalNumbers}</b>
      </div>
      <div className="mt-1 text-sm text-slate-300">
        Amount:{" "}
        <b>
          {sym}
          {bundle.totalAmount.toFixed(2)}
        </b>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Link
          to={`/lottery/pay/bundle/${encodeURIComponent(bundle.key)}`}
          className="px-3 py-2 rounded-xl bg-lime-400 text-black font-semibold"
        >
          Pay now & upload slip
        </Link>

        {onWhatsApp ? (
          <button
            onClick={() => onWhatsApp(bundle)}
            className="px-3 py-2 rounded-xl bg-lime-300/80 text-black font-semibold"
          >
            WhatsApp
          </button>
        ) : null}
      </div>

      <div className="mt-2 text-[11px] text-slate-400">
        Tickets: {bundle.tickets.map((t) => t.id.slice(0, 6)).join(", ")}
      </div>
    </div>
  );
}

/* ───────── Page ───────── */
export default function MyTickets({ user }) {
  const [me, setMe] = useState(null); // my user profile (to read phone)
  const [tickets, setTickets] = useState([]); // merged tickets
  const [cards, setCards] = useState({});
  const cardUnsubsRef = useRef({});
  const ticketUnsubsRef = useRef([]);

  // agent modal
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentUid, setAgentUid] = useState(null);
  const [agentCache, setAgentCache] = useState({}); // uid -> data
  const [agentLoading, setAgentLoading] = useState(false);

  const openAgent = async (uid) => {
    setAgentUid(uid);
    setAgentOpen(true);
    if (agentCache[uid]) return;
    setAgentLoading(true);
    try {
      const s = await getDoc(doc(db, "agents", uid));
      if (s.exists()) {
        setAgentCache((m) => ({ ...m, [uid]: { id: s.id, ...s.data() } }));
      } else {
        setAgentCache((m) => ({ ...m, [uid]: null }));
      }
    } finally {
      setAgentLoading(false);
    }
  };

  // Load my profile once (to get phoneE164 / phone)
  useEffect(() => {
    if (!user?.uid) return;
    const ref = doc(db, "users", user.uid);
    getDoc(ref)
      .then((s) => s.exists() && setMe({ id: s.id, ...s.data() }))
      .catch(() => {});
  }, [user?.uid]);

  // Subscribe to my tickets by uid and by mobile (agent allocations)
  useEffect(() => {
    ticketUnsubsRef.current.forEach((fn) => fn && fn());
    ticketUnsubsRef.current = [];
    setTickets([]);

    if (!user?.uid) return;

    const uid = user.uid;
    const phoneE164 = me?.phoneE164 || me?.phone || me?.mobile || null;
    const digits = (String(me?.phone || me?.mobile || "") || "").replace(
      /\D/g,
      ""
    );

    const mergeFrom = (q) =>
      onSnapshot(q, (snap) => {
        setTickets((prev) => {
          const map = new Map(prev.map((t) => [t.id, t]));
          snap.forEach((d) => {
            const incoming = { id: d.id, ...d.data() };
            const existing = map.get(d.id);
            if (!existing) {
              map.set(d.id, incoming);
            } else {
              // keep the freshest by lastChangeTs
              map.set(
                d.id,
                lastChangeTs(incoming) >= lastChangeTs(existing)
                  ? incoming
                  : existing
              );
            }
          });
          return Array.from(map.values());
        });
      });

    ticketUnsubsRef.current.push(
      mergeFrom(query(collection(db, "tickets"), where("userUid", "==", uid)))
    );

    if (phoneE164) {
      ticketUnsubsRef.current.push(
        mergeFrom(
          query(
            collection(db, "tickets"),
            where("userMobileE164", "==", phoneE164)
          )
        )
      );
    }

    if (digits) {
      ticketUnsubsRef.current.push(
        mergeFrom(
          query(collection(db, "tickets"), where("userMobile", "==", digits))
        )
      );
      ticketUnsubsRef.current.push(
        mergeFrom(
          query(
            collection(db, "tickets"),
            where("userMobile", "==", phoneE164 || "")
          )
        )
      );
    }

    return () => {
      ticketUnsubsRef.current.forEach((fn) => fn && fn());
      ticketUnsubsRef.current = [];
    };
  }, [user?.uid, me?.phoneE164, me?.phone, me?.mobile]);

  // Subscribe to cards referenced by current tickets
  useEffect(() => {
    const seen = new Set();
    tickets.forEach((t) => t.cardId && seen.add(t.cardId));

    const currentSubs = cardUnsubsRef.current;
    Object.keys(currentSubs).forEach((id) => {
      if (!seen.has(id)) {
        currentSubs[id]();
        delete currentSubs[id];
      }
    });
    seen.forEach((id) => {
      if (!currentSubs[id]) {
        currentSubs[id] = onDocSnap(doc(db, "cards", id), (snapCard) => {
          setCards((prev) => ({
            ...prev,
            [id]: snapCard.exists()
              ? { id: snapCard.id, ...snapCard.data() }
              : null,
          }));
        });
      }
    });

    return () => {};
  }, [tickets]);

  // Split into active vs history (after final merge). We sort newest first using best timestamp.
  const { active, history } = useMemo(() => {
    const getTs = (t) =>
      t.requestedAt?.toDate?.()?.getTime?.() ||
      t.createdAt?.toDate?.()?.getTime?.() ||
      t.allocatedAt?.toDate?.()?.getTime?.() ||
      t.paymentApprovedAt?.toDate?.()?.getTime?.() ||
      0;

    const arr = [...tickets].sort((a, b) => getTs(b) - getTs(a));
    const activeList = [];
    const historyList = [];

    for (const t of arr) {
      const card = cards[t.cardId];
      const cardStatus = card?.status;
      const isFinalTicket =
        t.status === "expired" ||
        t.status === "payment_declined" ||
        t.status === "void_drawn";
      const cardDrawn = cardStatus === "drawn";

      if (!isFinalTicket && !cardDrawn) activeList.push({ t, card });
      else historyList.push({ t, card });
    }

    return { active: activeList, history: historyList };
  }, [tickets, cards]);

  const activeNonBundle = useMemo(
    () => active.filter(({ t }) => t.status !== "agent_approved"),
    [active]
  );

  // Group all agent_approved tickets into payment bundles (same agent/user)
  const paymentBundles = useMemo(() => {
    const byKey = new Map();
    for (const t of tickets) {
      if (t.status !== "agent_approved") continue;

      // Prefer stored bundleId; else derive one (agent|user)
      const fallbackUserKey =
        t.userUid || t.userMobileE164 || t.userMobile || "unknown";
      const key = t.bundleId || `${t.agentUid || "agent"}|${fallbackUserKey}`;

      const entry = byKey.get(key) || {
        key,
        agentUid: t.agentUid || null,
        agentName: t.agentName || "Agent",
        tickets: [],
        totalAmount: 0,
        totalNumbers: 0,
        earliestExpireMs: 0,
        firstTicketId: t.id,
      };

      const nums = Array.isArray(t.numbers) ? t.numbers : [];
      const unit = Number(t.unitPrice || 0);
      const amt = Number(t.amount || unit * nums.length);

      entry.tickets.push(t);
      entry.totalAmount += amt;
      entry.totalNumbers += nums.length;

      const exp = tsMs(t.paymentExpireAt);
      entry.earliestExpireMs =
        entry.earliestExpireMs === 0
          ? exp
          : Math.min(entry.earliestExpireMs, exp);

      if (!entry.firstTicketId) entry.firstTicketId = t.id;

      byKey.set(key, entry);
    }
    return Array.from(byKey.values());
  }, [tickets]);

  // Hide today's numbers strip if today's draw is done (any relevant card for today is drawn or time passed)
  const hideTodaySummary = useMemo(() => {
    const now = Date.now();
    const today = new Date();

    const toJsDate = (v) => v?.toDate?.() || (v ? new Date(v) : null);

    return Object.values(cards).some((card) => {
      if (!card) return false;
      const d = toJsDate(card.drawAt);
      if (!d || !isSameDay(d, today)) return false;
      return card.status === "drawn" || d.getTime() <= now;
    });
  }, [cards]);

  // Today’s numbers summary
  const todayNumberFreq = useMemo(
    () => buildTodayNumberFreq(tickets),
    [tickets]
  );

  const currentAgent = agentUid ? agentCache[agentUid] : null;

  return (
    <div className="max-w-5xl mx-auto mt-8 md:mt-24">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h1 className="text-2xl font-bold">My Tickets</h1>

        {/* Today’s numbers summary strip */}
        <div className="hidden md:flex items-center gap-2 flex-wrap justify-end">
          {!hideTodaySummary && todayNumberFreq.length ? (
            <>
              <span className="text-xs text-slate-400 mr-1">Today:</span>
              <div className="flex items-center gap-2 max-w-[50vw] overflow-x-auto no-scrollbar">
                {todayNumberFreq.map(({ num, cnt }) => (
                  <NumberStatPill key={num} n={num} c={cnt} />
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* In Progress */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-2">In Progress</h2>

        {/* Bundled payments */}
        {paymentBundles.length ? (
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {paymentBundles.map((b) => (
              <PayBundleRow
                key={b.key}
                bundle={b}
                currencyCode={cards[b.tickets[0]?.cardId]?.currency || "LKR"}
              />
            ))}
          </div>
        ) : null}

        {/* Other active tickets */}
        <div className="grid md:grid-cols-2 gap-4">
          {activeNonBundle.map(({ t, card }) => (
            <TicketRowActive
              key={t.id}
              t={t}
              card={card}
              onAgentOpen={openAgent}
            />
          ))}
        </div>

        {!paymentBundles.length && !activeNonBundle.length && (
          <div className="p-4 rounded-xl bg-slate-800 text-slate-300">
            No active tickets.
          </div>
        )}
      </section>

      {/* History */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-2">History</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {history.map(({ t, card }) => (
            <TicketRowHistory
              key={t.id}
              t={t}
              card={card}
              onAgentOpen={openAgent}
            />
          ))}
        </div>
        {!history.length && (
          <div className="p-4 rounded-xl bg-slate-800 text-slate-300">
            No history yet.
          </div>
        )}
      </section>

      {/* Mobile-only summary footer */}
      <div className="md:hidden mt-2 mb-8">
        {!hideTodaySummary && todayNumberFreq.length ? (
          <>
            <div className="text-xs text-slate-400 mb-1">Today’s numbers</div>
            <div className="flex items-center gap-2 flex-wrap">
              {todayNumberFreq.map(({ num, cnt }) => (
                <NumberStatPill key={num} n={num} c={cnt} />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {/* Agent modal */}
      {agentOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setAgentOpen(false)}
        >
          <div
            className="absolute right-0 top-0 h-full w-full max-w-md bg-slate-900 p-5 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-semibold">Agent</div>
              <button
                onClick={() => setAgentOpen(false)}
                className="px-3 py-1 rounded bg-slate-800"
              >
                Close
              </button>
            </div>

            {agentLoading && !currentAgent ? (
              <div className="text-slate-300">Loading…</div>
            ) : currentAgent ? (
              <div className="space-y-2 text-sm text-slate-300">
                <div className="text-xl font-bold text-white">
                  {currentAgent.displayName ||
                    currentAgent.name ||
                    currentAgent.username ||
                    "Agent"}
                </div>
                <div className="text-slate-400">
                  Country: {currentAgent.country || "—"}
                  {currentAgent.district ? ` • ${currentAgent.district}` : ""}
                </div>
                {currentAgent.whatsApp ||
                currentAgent.whatsapp ||
                currentAgent.phone ? (
                  <div>
                    WhatsApp:{" "}
                    <b>
                      {currentAgent.whatsApp ||
                        currentAgent.whatsapp ||
                        currentAgent.phone}
                    </b>
                  </div>
                ) : null}
                {currentAgent.binancePayId ? (
                  <div>Binance Pay ID: {currentAgent.binancePayId}</div>
                ) : null}

                <div className="mt-3">
                  <div className="text-sm font-semibold mb-1">Bank methods</div>
                  {Array.isArray(currentAgent.banks) &&
                  currentAgent.banks.length ? (
                    <div className="space-y-2">
                      {currentAgent.banks.map((b) => (
                        <div
                          key={b._id || `${b.bankName}_${b.accountNumber}`}
                          className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700"
                        >
                          <div>{b.bankName}</div>
                          <div className="text-slate-300">
                            {b.accountName} · {b.accountNumber}
                            {b.branch ? ` · ${b.branch}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-400">No bank info.</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-slate-300">Agent not found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
